import { useEffect, useRef, useState, useCallback } from 'react'
import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

interface CameraModalProps {
    open: boolean
    onCapture: (blob: Blob) => void
    onClose: () => void
}

/** Returns true only when getUserMedia is available (requires HTTPS or localhost). */
function isCameraApiAvailable(): boolean {
    return window.isSecureContext && !!navigator.mediaDevices?.getUserMedia
}

export function CameraModal({ open, onCapture, onClose }: CameraModalProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const fallbackInputRef = useRef<HTMLInputElement>(null)
    const [ready, setReady] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [flash, setFlash] = useState(false)
    const [useFallback, setUseFallback] = useState(false)

    useEffect(() => {
        if (!open) return
        setError(null)
        setReady(false)
        setUseFallback(false)

        if (!isCameraApiAvailable()) {
            setUseFallback(true)
            return
        }

        let cancelled = false

        const startStream = async () => {
            try {
                let stream: MediaStream
                try {
                    // Full constraints — may be rejected on older iOS (OverconstrainedError)
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
                        audio: false,
                    })
                } catch {
                    // Retry with minimal constraints for iOS compatibility
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: 'environment' },
                        audio: false,
                    })
                }
                if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
                streamRef.current = stream
                if (videoRef.current) videoRef.current.srcObject = stream
            } catch (err: unknown) {
                if (!cancelled) setError((err as Error)?.message ?? getMessage('camera.status_unavailable'))
            }
        }

        startStream()
        return () => { cancelled = true; stopStream() }
    }, [open])

    const stopStream = () => {
        streamRef.current?.getTracks().forEach(t => t.stop())
        streamRef.current = null
        setReady(false)
    }

    const handleClose = () => { stopStream(); onClose() }

    const handleCapture = useCallback(() => {
        const video = videoRef.current
        if (!video || !ready) return

        // Guard against iOS firing canplay before dimensions are populated
        const w = video.videoWidth || video.clientWidth
        const h = video.videoHeight || video.clientHeight
        if (!w || !h) return

        setFlash(true)
        setTimeout(() => setFlash(false), 150)

        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(video, 0, 0)

        stopStream()
        // JPEG is faster to encode on mobile and produces smaller uploads
        canvas.toBlob((blob) => {
            if (blob) onCapture(blob)
            onClose()
        }, 'image/jpeg', 0.92)
    }, [ready, onCapture, onClose])

    const handleFallbackChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            onCapture(file)
            onClose()
        }
    }, [onCapture, onClose])

    if (!open) return null

    return (
        <div className="fixed inset-0 z-[500] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

            <div
                className={cn(
                    'relative z-10 w-full max-w-[480px] mx-4',
                    'bg-[var(--surface)] border border-[var(--border)]',
                    'rounded-[var(--radius)] overflow-hidden',
                    'shadow-[0_20px_60px_rgba(0,0,0,0.18)]',
                    'animate-fade-in'
                )}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ─────────────────────────────────────────── */}
                <div className={cn(
                    'flex items-center justify-between',
                    'px-5 py-4 border-b border-[var(--border)]',
                    'bg-[var(--surface)]'
                )}>
                    <div className="flex items-center gap-[10px]">
                        <div
                            className={cn(
                                'w-8 h-8 rounded-[8px] flex items-center justify-center text-[15px]',
                                'bg-[rgba(61,142,240,0.1)] border border-[rgba(61,142,240,0.2)]'
                            )}
                        >
                            📷
                        </div>
                        <span
                            className="text-[14px] font-bold text-[var(--text)]"
                            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
                        >
                            {getMessage('camera.title')}
                        </span>
                    </div>

                    <button
                        onClick={handleClose}
                        className={cn(
                            'w-7 h-7 rounded-[6px] border border-[var(--border)]',
                            'bg-transparent text-[var(--text-muted)] text-[13px]',
                            'flex items-center justify-center cursor-pointer',
                            'hover:bg-[var(--surface3)] hover:text-[var(--text)]',
                            'transition-all duration-150'
                        )}
                    >
                        ✕
                    </button>
                </div>

                {/* ── Video / Fallback area ───────────────────────────── */}
                <div className="relative bg-[#0a0a0a] aspect-video flex items-center justify-center overflow-hidden">
                    {useFallback ? (
                        <div className="flex flex-col items-center gap-4 px-8 text-center">
                            <div className={cn(
                                'w-10 h-10 rounded-full flex items-center justify-center',
                                'bg-[rgba(61,142,240,0.1)] border border-[rgba(61,142,240,0.2)]'
                            )}>
                                <span className="text-[18px]">📷</span>
                            </div>
                            <p className="text-[12px] text-white/60 font-mono leading-relaxed">
                                {getMessage('camera.fallback_hint')}
                            </p>
                            <button
                                onClick={() => fallbackInputRef.current?.click()}
                                className={cn(
                                    'flex items-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
                                    'font-mono text-[12px] font-semibold text-white cursor-pointer',
                                    'hover:opacity-90 transition-all duration-[180ms]'
                                )}
                                style={{ background: 'linear-gradient(135deg, var(--accent), #2563eb)' }}
                            >
                                {getMessage('camera.fallback_button')}
                            </button>
                            <input
                                ref={fallbackInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={handleFallbackChange}
                            />
                        </div>
                    ) : (
                        <>
                            {flash && (
                                <div className="absolute inset-0 bg-white opacity-70 z-10 pointer-events-none transition-opacity" />
                            )}

                            {error ? (
                                <div className="flex flex-col items-center gap-3 px-8 text-center">
                                    <div className={cn(
                                        'w-10 h-10 rounded-full flex items-center justify-center',
                                        'bg-[rgba(220,38,38,0.1)] border border-[rgba(220,38,38,0.2)]'
                                    )}>
                                        <span className="text-[18px]">⚠️</span>
                                    </div>
                                    <p className="text-[13px] font-semibold text-[var(--error)]">{error}</p>
                                    <p className="text-[11px] text-[var(--text-muted)] font-mono">
                                        {getMessage('camera.permission_hint')}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        onCanPlay={() => setReady(true)}
                                        className="w-full h-full object-cover"
                                    />

                                    {ready && (
                                        <div className="absolute inset-0 pointer-events-none">
                                            <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-white/60 rounded-tl-sm" />
                                            <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-white/60 rounded-tr-sm" />
                                            <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-white/60 rounded-bl-sm" />
                                            <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-white/60 rounded-br-sm" />
                                        </div>
                                    )}

                                    {!ready && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                                            <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                                            <span className="text-[11px] text-white/50 font-mono tracking-wide">
                                                {getMessage('camera.status_starting')}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* ── Action bar ─────────────────────────────────────── */}
                <div className={cn(
                    'flex items-center justify-between',
                    'px-5 py-4 border-t border-[var(--border)]',
                    'bg-[var(--surface2)]'
                )}>
                    <button
                        onClick={handleClose}
                        className={cn(
                            'flex items-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
                            'border border-[var(--border)] bg-[var(--surface)]',
                            'text-[var(--text-dim)] font-mono text-[12px] font-semibold',
                            'hover:border-[var(--error)] hover:text-[var(--error)]',
                            'transition-all duration-[180ms] cursor-pointer'
                        )}
                    >
                        {getMessage('common.button.cancel')}
                    </button>

                    <span className="text-[10px] font-mono text-[var(--text-muted)] tracking-wide">
                        {useFallback
                            ? ''
                            : error
                                ? getMessage('camera.status_unavailable')
                                : ready
                                    ? getMessage('camera.status_ready')
                                    : getMessage('camera.status_init')}
                    </span>

                    {/* Capture button hidden in fallback mode — the fallback area has its own trigger */}
                    {!useFallback && (
                        <button
                            onClick={handleCapture}
                            disabled={!ready || !!error}
                            className={cn(
                                'flex items-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
                                'font-mono text-[12px] font-semibold text-white',
                                'border border-transparent transition-all duration-[180ms]',
                                ready && !error
                                    ? 'cursor-pointer hover:opacity-90'
                                    : 'opacity-40 cursor-not-allowed'
                            )}
                            style={{
                                background: ready && !error
                                    ? 'linear-gradient(135deg, var(--accent), #2563eb)'
                                    : 'var(--surface3)',
                                color: ready && !error ? 'white' : 'var(--text-muted)',
                            }}
                        >
                            {getMessage('camera.button_capture')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
